/**
 * EligibilityChecklistPanel.tsx — the agentic editor's Eligibility &
 * checklist tab (ADR-085 D6). Edits `doc.eligibility` and
 * `doc.document_checklist` (ADR-085 D1), Admin-gated the same way as
 * Persona/Boundaries/Scripts/Facts (ADR-084 D3 — server enforces, this tab
 * is hidden client-side for non-Admin, same as those).
 *
 * Eligibility section (Aron's ruling 1): `age_limit_years`,
 * `weight_limit_tonnes`, and `reask_attempts` are fully editable thresholds.
 * The four builtin question keys (`ELIGIBILITY_QUESTION_KEYS`, D5 rule 13)
 * are fixed, no add/remove control. Per question, only `prompt` (wording)
 * and each existing option label's recognised-token list (answer options)
 * are editable — `pass_label`/`fail_reasons`/`uncertain_labels` stay
 * read-only: they are the structural mapping `_labels_consistent` (the
 * shared model) enforces, and the label *set* itself is exactly what rule
 * 13 pins to the four builtin keys' shape.
 *
 * Document-checklist section (Aron's ruling 2): add AND remove, following
 * `BoundariesTab.tsx`'s plain repeatable-list pattern (index-keyed — a
 * checklist entry's `key` is itself a free-text field, so it cannot double
 * as a stable React/list id the way `ReorderableList` needs; reorder is not
 * in this card's acceptance criteria, so `document_checklist.sequence`
 * follows array order only, no drag/reorder control here).
 *
 * `early_simulation` stays read-only JSON from `graph_config_json`, same
 * rendering `NodeConfigBlock` used before this slice.
 */

import { useTranslation } from 'react-i18next';
import {
  AGENT_CONFIG_MAX_LENGTHS,
  DOCUMENT_TYPES,
  ELIGIBILITY_QUESTION_KEYS,
  type DocumentChecklistConfig,
  type DocumentType,
  type SkillVersionFieldError,
  type VehicleEligibilityConfig,
} from '../../api/types';
import { CounterField, textActionClass, textActionDangerClass } from './agenticFieldHelpers';
import { inputClass } from '../../components/Field';

const EARLY_SIMULATION_NODE_NAME = 'early_simulation';

const nodeConfig = (
  graphConfigJson: Record<string, unknown>,
  nodeName: string,
): Record<string, unknown> | null => {
  const { nodes } = graphConfigJson;
  if (!Array.isArray(nodes)) return null;
  const match = nodes.find(
    (node) =>
      node != null && typeof node === 'object' && (node as { name?: unknown }).name === nodeName,
  ) as { config?: unknown } | undefined;
  return match?.config != null && typeof match.config === 'object'
    ? (match.config as Record<string, unknown>)
    : null;
};

const errorFor = (fieldErrors: SkillVersionFieldError[], path: string): string | undefined =>
  fieldErrors.find((error) => error.field_path === path)?.reason;

interface ChecklistEntry {
  key: string;
  type: string;
  template: string;
}

const checklistEntries = (config: DocumentChecklistConfig): ChecklistEntry[] =>
  config.sequence.map((key) => ({
    key,
    type: config.types[key] ?? '',
    template: config.request_templates[key] ?? '',
  }));

const entriesToConfig = (entries: ChecklistEntry[]): DocumentChecklistConfig => ({
  sequence: entries.map((entry) => entry.key),
  types: Object.fromEntries(entries.map((entry) => [entry.key, entry.type])),
  request_templates: Object.fromEntries(entries.map((entry) => [entry.key, entry.template])),
});

export interface EligibilityChecklistPanelProps {
  graphConfigJson: Record<string, unknown>;
  eligibility: VehicleEligibilityConfig | null;
  documentChecklist: DocumentChecklistConfig | null;
  onChangeEligibility: (next: VehicleEligibilityConfig) => void;
  onChangeChecklist: (next: DocumentChecklistConfig) => void;
  disabled?: boolean;
  fieldErrors: SkillVersionFieldError[];
}

export const EligibilityChecklistPanel = ({
  graphConfigJson,
  eligibility,
  documentChecklist,
  onChangeEligibility,
  onChangeChecklist,
  disabled = undefined,
  fieldErrors,
}: EligibilityChecklistPanelProps) => {
  const { t } = useTranslation();

  const updateQuestion = (
    key: string,
    patch: { prompt?: string; options?: Record<string, string[]> },
  ) => {
    if (eligibility == null) return;
    const question = eligibility.questions[key];
    if (question == null) return;
    onChangeEligibility({
      ...eligibility,
      questions: { ...eligibility.questions, [key]: { ...question, ...patch } },
    });
  };

  const updateOptionTokens = (questionKey: string, label: string, tokensCsv: string) => {
    const question = eligibility?.questions[questionKey];
    if (question == null) return;
    const tokens = tokensCsv
      .split(',')
      .map((token) => token.trim())
      .filter((token) => token !== '');
    updateQuestion(questionKey, { options: { ...question.options, [label]: tokens } });
  };

  const updateEligibilityField = (
    patch: Partial<
      Pick<VehicleEligibilityConfig, 'age_limit_years' | 'weight_limit_tonnes' | 'reask_attempts'>
    >,
  ) => {
    if (eligibility == null) return;
    onChangeEligibility({ ...eligibility, ...patch });
  };

  const entries = documentChecklist != null ? checklistEntries(documentChecklist) : [];
  const updateEntry = (index: number, patch: Partial<ChecklistEntry>) => {
    const next = entries.map((entry, i) => (i === index ? { ...entry, ...patch } : entry));
    onChangeChecklist(entriesToConfig(next));
  };
  const removeEntry = (index: number) =>
    onChangeChecklist(entriesToConfig(entries.filter((_, i) => i !== index)));
  const addEntry = () =>
    onChangeChecklist(
      entriesToConfig([...entries, { key: '', type: DOCUMENT_TYPES[0], template: '' }]),
    );

  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="agentic-eligibility-heading" className="flex flex-col gap-4">
        <h3 id="agentic-eligibility-heading" className="text-h3 text-ink-1">
          {t('config.editAgentic.eligibility.heading')}
        </h3>

        {eligibility == null ? (
          <p data-testid="agentic-eligibility-vehicle-missing" className="text-meta text-ink-3">
            {t('config.editAgentic.eligibility.missing')}
          </p>
        ) : (
          <>
            {errorFor(fieldErrors, 'eligibility') != null ? (
              <p
                role="alert"
                data-testid="agentic-eligibility-error"
                className="text-meta text-status-down"
              >
                {errorFor(fieldErrors, 'eligibility')}
              </p>
            ) : null}
            {errorFor(fieldErrors, 'eligibility.questions') != null ? (
              <p
                role="alert"
                data-testid="agentic-eligibility-questions-error"
                className="text-meta text-status-down"
              >
                {errorFor(fieldErrors, 'eligibility.questions')}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-4">
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="agentic-eligibility-age-limit"
                  className="text-meta font-medium text-ink-2"
                >
                  {t('config.editAgentic.eligibility.ageLimitYears')}
                </label>
                <input
                  id="agentic-eligibility-age-limit"
                  data-testid="agentic-eligibility-age-limit"
                  type="number"
                  min={1}
                  max={40}
                  value={eligibility.age_limit_years}
                  disabled={disabled}
                  onChange={(event) =>
                    updateEligibilityField({ age_limit_years: Number(event.target.value) })
                  }
                  className={inputClass}
                />
                {errorFor(fieldErrors, 'eligibility.age_limit_years') != null ? (
                  <p
                    role="alert"
                    data-testid="agentic-eligibility-age-limit-error"
                    className="text-meta text-status-down"
                  >
                    {errorFor(fieldErrors, 'eligibility.age_limit_years')}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="agentic-eligibility-weight-limit"
                  className="text-meta font-medium text-ink-2"
                >
                  {t('config.editAgentic.eligibility.weightLimitTonnes')}
                </label>
                <input
                  id="agentic-eligibility-weight-limit"
                  data-testid="agentic-eligibility-weight-limit"
                  type="number"
                  min={0.5}
                  max={80}
                  step={0.1}
                  value={eligibility.weight_limit_tonnes}
                  disabled={disabled}
                  onChange={(event) =>
                    updateEligibilityField({ weight_limit_tonnes: Number(event.target.value) })
                  }
                  className={inputClass}
                />
                {errorFor(fieldErrors, 'eligibility.weight_limit_tonnes') != null ? (
                  <p
                    role="alert"
                    data-testid="agentic-eligibility-weight-limit-error"
                    className="text-meta text-status-down"
                  >
                    {errorFor(fieldErrors, 'eligibility.weight_limit_tonnes')}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="agentic-eligibility-reask-attempts"
                  className="text-meta font-medium text-ink-2"
                >
                  {t('config.editAgentic.eligibility.reaskAttempts')}
                </label>
                <input
                  id="agentic-eligibility-reask-attempts"
                  data-testid="agentic-eligibility-reask-attempts"
                  type="number"
                  min={0}
                  max={5}
                  value={eligibility.reask_attempts}
                  disabled={disabled}
                  onChange={(event) =>
                    updateEligibilityField({ reask_attempts: Number(event.target.value) })
                  }
                  className={inputClass}
                />
                {errorFor(fieldErrors, 'eligibility.reask_attempts') != null ? (
                  <p
                    role="alert"
                    data-testid="agentic-eligibility-reask-attempts-error"
                    className="text-meta text-status-down"
                  >
                    {errorFor(fieldErrors, 'eligibility.reask_attempts')}
                  </p>
                ) : null}
              </div>
            </div>

            {ELIGIBILITY_QUESTION_KEYS.filter((key) => eligibility.questions[key] != null).map(
              (key) => {
                const question = eligibility.questions[key]!;
                return (
                  <div
                    key={key}
                    data-testid={`agentic-eligibility-question-${key}`}
                    className="flex flex-col gap-3 rounded-lg bg-surface-2 p-4 ring-1 ring-inset ring-ink-3/10"
                  >
                    <h4 className="text-body font-medium text-ink-1">
                      {t('config.editAgentic.eligibility.questionHeading', { key })}
                    </h4>
                    <CounterField
                      id={`agentic-eligibility-question-${key}-prompt`}
                      label={t('config.editAgentic.eligibility.prompt')}
                      value={question.prompt}
                      maxLength={AGENT_CONFIG_MAX_LENGTHS.eligibilityPrompt}
                      onChange={(next) => updateQuestion(key, { prompt: next })}
                      disabled={disabled}
                      multiline
                      rows={2}
                      error={errorFor(fieldErrors, `eligibility.questions[${key}].prompt`)}
                    />
                    <div className="flex flex-col gap-2">
                      <span className="text-meta font-medium text-ink-2">
                        {t('config.editAgentic.eligibility.options')}
                      </span>
                      {Object.keys(question.options).map((label) => (
                        <div key={label} className="flex flex-col gap-0.5">
                          <label
                            htmlFor={`agentic-eligibility-question-${key}-option-${label}`}
                            className="text-meta text-ink-3"
                          >
                            {label}
                            {label === question.pass_label
                              ? ` (${t('config.editAgentic.eligibility.passLabel')})`
                              : ''}
                          </label>
                          <input
                            id={`agentic-eligibility-question-${key}-option-${label}`}
                            data-testid={`agentic-eligibility-question-${key}-option-${label}`}
                            type="text"
                            value={question.options[label]!.join(', ')}
                            maxLength={AGENT_CONFIG_MAX_LENGTHS.eligibilityOptionToken}
                            disabled={disabled}
                            onChange={(event) => updateOptionTokens(key, label, event.target.value)}
                            className={`${inputClass} font-mono`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              },
            )}
          </>
        )}
      </section>

      <section aria-labelledby="agentic-checklist-heading" className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 id="agentic-checklist-heading" className="text-h3 text-ink-1">
            {t('config.editAgentic.eligibility.checklistHeading')}
          </h3>
          {documentChecklist != null ? (
            <button
              type="button"
              data-testid="agentic-checklist-add"
              disabled={disabled}
              onClick={addEntry}
              className={textActionClass}
            >
              {t('config.editAgentic.eligibility.checklistAdd')}
            </button>
          ) : null}
        </div>

        {documentChecklist == null ? (
          <p data-testid="agentic-checklist-missing" className="text-meta text-ink-3">
            {t('config.editAgentic.eligibility.missing')}
          </p>
        ) : (
          <>
            {errorFor(fieldErrors, 'document_checklist') != null ? (
              <p
                role="alert"
                data-testid="agentic-checklist-error"
                className="text-meta text-status-down"
              >
                {errorFor(fieldErrors, 'document_checklist')}
              </p>
            ) : null}
            {errorFor(fieldErrors, 'document_checklist.types') != null ? (
              <p
                role="alert"
                data-testid="agentic-checklist-types-error"
                className="text-meta text-status-down"
              >
                {errorFor(fieldErrors, 'document_checklist.types')}
              </p>
            ) : null}

            {entries.length === 0 ? (
              <p data-testid="agentic-checklist-empty" className="text-body text-ink-2">
                {t('config.editAgentic.eligibility.checklistEmpty')}
              </p>
            ) : (
              entries.map((entry, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key -- rows have no stable id; revisit in slice 11
                  key={index}
                  data-testid={`agentic-checklist-entry-${index}`}
                  className="flex flex-col gap-2 rounded-lg bg-surface-2 p-3 ring-1 ring-inset ring-ink-3/10"
                >
                  <div className="flex items-center justify-between gap-2">
                    <label
                      htmlFor={`agentic-checklist-entry-${index}-key`}
                      className="text-meta font-medium text-ink-2"
                    >
                      {t('config.editAgentic.eligibility.checklistKey')}
                    </label>
                    <button
                      type="button"
                      data-testid={`agentic-checklist-entry-${index}-remove`}
                      disabled={disabled}
                      onClick={() => removeEntry(index)}
                      className={textActionDangerClass}
                    >
                      {t('config.editAgentic.eligibility.checklistRemove')}
                    </button>
                  </div>
                  <input
                    id={`agentic-checklist-entry-${index}-key`}
                    data-testid={`agentic-checklist-entry-${index}-key`}
                    type="text"
                    value={entry.key}
                    disabled={disabled}
                    onChange={(event) => updateEntry(index, { key: event.target.value })}
                    className={`${inputClass} font-mono`}
                  />
                  {errorFor(fieldErrors, `document_checklist.sequence[${entry.key}]`) != null ? (
                    <p
                      role="alert"
                      data-testid={`agentic-checklist-entry-${index}-key-error`}
                      className="text-meta text-status-down"
                    >
                      {errorFor(fieldErrors, `document_checklist.sequence[${entry.key}]`)}
                    </p>
                  ) : null}

                  <label
                    htmlFor={`agentic-checklist-entry-${index}-type`}
                    className="text-meta font-medium text-ink-2"
                  >
                    {t('config.editAgentic.eligibility.checklistType')}
                  </label>
                  <select
                    id={`agentic-checklist-entry-${index}-type`}
                    data-testid={`agentic-checklist-entry-${index}-type`}
                    value={entry.type}
                    disabled={disabled}
                    onChange={(event) =>
                      updateEntry(index, { type: event.target.value as DocumentType })
                    }
                    className={inputClass}
                  >
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>

                  <CounterField
                    id={`agentic-checklist-entry-${index}-template`}
                    label={t('config.editAgentic.eligibility.checklistTemplate')}
                    value={entry.template}
                    maxLength={AGENT_CONFIG_MAX_LENGTHS.checklistRequestTemplate}
                    onChange={(next) => updateEntry(index, { template: next })}
                    disabled={disabled}
                    multiline
                    rows={2}
                    error={errorFor(
                      fieldErrors,
                      `document_checklist.request_templates[${entry.key}]`,
                    )}
                  />
                </div>
              ))
            )}
          </>
        )}
      </section>

      <section aria-labelledby="agentic-early-simulation-heading" className="flex flex-col gap-1">
        <h4 id="agentic-early-simulation-heading" className="text-body font-medium text-ink-1">
          {t('config.editAgentic.eligibility.earlySimulation')}
        </h4>
        <p className="text-meta text-ink-3">
          {t('config.editAgentic.eligibility.earlySimulationNotice')}
        </p>
        {(() => {
          const config = nodeConfig(graphConfigJson, EARLY_SIMULATION_NODE_NAME);
          return config == null ? (
            <p
              data-testid="agentic-eligibility-simulation-missing"
              className="text-meta text-ink-3"
            >
              {t('config.editAgentic.eligibility.missing')}
            </p>
          ) : (
            <pre
              data-testid="agentic-eligibility-simulation"
              aria-readonly="true"
              className="max-h-96 overflow-auto rounded-lg bg-surface-1 p-3 font-mono text-meta text-ink-2 ring-1 ring-inset ring-ink-3/20"
            >
              {JSON.stringify(config, null, 2)}
            </pre>
          );
        })()}
      </section>
    </div>
  );
};
